import axios from 'axios'

const MIN_REQUEST_INTERVAL_MS = 1100
const MAX_RETRY_DELAY_MS = 60000

let requestQueue = Promise.resolve()
let lastRequestAt = 0
const pendingRequests = new Map()

const wait = (duration) => new Promise((resolve) => setTimeout(resolve, duration))

const getRetryDelay = (error) => {
  const retryAfter = error?.response?.headers?.['retry-after']
  const seconds = Number(retryAfter)

  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, MAX_RETRY_DELAY_MS)
  }

  const retryDate = Date.parse(retryAfter || '')
  if (!Number.isNaN(retryDate)) {
    return Math.min(Math.max(retryDate - Date.now(), 0), MAX_RETRY_DELAY_MS)
  }

  return 5000
}

const requestWithBackoff = async (requestConfig) => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const elapsed = Date.now() - lastRequestAt
    if (elapsed < MIN_REQUEST_INTERVAL_MS) {
      await wait(MIN_REQUEST_INTERVAL_MS - elapsed)
    }

    lastRequestAt = Date.now()

    try {
      return await axios(requestConfig)
    } catch (error) {
      if (error?.response?.status !== 429 || attempt === 1) {
        throw error
      }

      await wait(getRetryDelay(error))
    }
  }

  throw new Error('Xero request failed after retry')
}

export const requestXero = (requestConfig) => {
  const requestKey = JSON.stringify({
    method: requestConfig.method || 'get',
    url: requestConfig.url,
    data: requestConfig.data || null,
    params: requestConfig.params || null,
  })

  const pending = pendingRequests.get(requestKey)
  if (pending) return pending

  const request = requestQueue.then(() => requestWithBackoff(requestConfig))
  requestQueue = request.catch(() => undefined)
  pendingRequests.set(requestKey, request)

  request.then(
    () => pendingRequests.delete(requestKey),
    () => pendingRequests.delete(requestKey),
  )

  return request
}

export const getXeroRequest = (url, config = {}) => requestXero({ ...config, method: 'get', url })

export const postXeroRequest = (url, data, config = {}) =>
  requestXero({ ...config, method: 'post', url, data })

export const putXeroRequest = (url, data, config = {}) =>
  requestXero({ ...config, method: 'put', url, data })
